import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useEffect, useState } from "react";
import { Bar, Pie } from "react-chartjs-2";
import { FaTools, FaStore, FaUserTie, FaUserFriends } from "react-icons/fa";
import Apiurl from "../environment/Apiurl";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  // Current month name
  const currentMonth = new Date().toLocaleString("default", { month: "long" });

  const [getBrandData, setGetBrandData] = useState([]);
  const [getStaffData, setGetStaffData] = useState([]);
  const [getCustomerDta, setGetCustomerDta] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(
      2,
      "0"
    )}`
  );
  const [barData, setBarData] = useState(null);
  const [pieData, setPieData] = useState(null);

  // Fetch data from APIs
  const fetchGetBrandData = async () => {
    try {
      const response = await axios.get(`${Apiurl}/brand`);
      setGetBrandData(response.data || []);
    } catch (error) {
      console.log(error.message);
    }
  };

  const fetchStaffData = async () => {
    try {
      const response = await axios.get(`${Apiurl}/staff`);
      setGetStaffData(response.data || []);
    } catch (error) {
      console.log(error.message);
    }
  };

  const fetchCustomerData = async () => {
    try {
      const response = await axios.get(`${Apiurl}/generalbill`);
      setGetCustomerDta(response.data || []);
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    fetchGetBrandData();
    fetchStaffData();
    fetchCustomerData();
  }, []);

  // Service counts for stats cards
  const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
  const currentYear = new Date().getFullYear();
  const currentMonthIndex = new Date().getMonth(); // 0-11

  const todayCount = getCustomerDta.filter((item) => {
    const itemDate = new Date(item.date).toISOString().split("T")[0];
    return itemDate === today;
  }).length;

  const monthCount = getCustomerDta.filter((item) => {
    const d = new Date(item.date);
    return (
      d.getFullYear() === currentYear && d.getMonth() === currentMonthIndex
    );
  }).length;

  // Compute chart data when customer data or selected month changes
  useEffect(() => {
    if (getCustomerDta.length > 0) {
      const brands = [...new Set(getCustomerDta.map((item) => item.brand))];
      const colors = ["#F50514", "#FFC915", "#074F71", "#FA471F"];
      const brandColors = {};
      brands.forEach((b, i) => {
        brandColors[b] = colors[i % colors.length];
      });

      const selectedYear = parseInt(selectedMonth.split("-")[0]);
      const selectedMonthIdx = parseInt(selectedMonth.split("-")[1]) - 1;

      // Bar chart computation: Data for the selected month only
      const monthlySums = {};
      brands.forEach((b) => {
        monthlySums[b] = Array(12).fill(0); // Initialize 12 months with zeros
      });

      getCustomerDta.forEach((item) => {
        const d = new Date(item.date);
        const y = d.getFullYear();
        const m = d.getMonth();
        if (
          y === selectedYear &&
          m === selectedMonthIdx &&
          brands.includes(item.brand)
        ) {
          monthlySums[item.brand][m] += item.net_total || 0;
        }
      });

      const barDatasets = brands.map((brand) => ({
        label: brand,
        data: monthlySums[brand],
        backgroundColor: brandColors[brand],
      }));

      const newBarData = {
        labels: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
        datasets: barDatasets,
      };
      setBarData(newBarData);

      // Pie chart computation (unchanged)
      const currentMonthSums = {};
      brands.forEach((b) => {
        currentMonthSums[b] = 0;
      });

      getCustomerDta.forEach((item) => {
        const d = new Date(item.date);
        const y = d.getFullYear();
        const m = d.getMonth();
        if (
          y === selectedYear &&
          m === selectedMonthIdx &&
          brands.includes(item.brand)
        ) {
          currentMonthSums[item.brand] += item.net_total || 0;
        }
      });

      const pieDatasets = [
        {
          data: brands.map((b) => currentMonthSums[b]),
          backgroundColor: brands.map((b) => brandColors[b]),
          borderColor: brands.map((b) => brandColors[b]),
          borderWidth: 1,
        },
      ];

      const newPieData = {
        labels: brands,
        datasets: pieDatasets,
      };
      setPieData(newPieData);
    }
  }, [getCustomerDta, selectedMonth]);

  const selectedYear = parseInt(selectedMonth.split("-")[0]);
  const selectedMonthIdx = parseInt(selectedMonth.split("-")[1]) - 1;
  const selectedMonthName = new Date(
    selectedYear,
    selectedMonthIdx
  ).toLocaleString("default", { month: "long" });

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: `Services Report (${selectedMonthName} ${selectedYear})`,
      },
    },
    scales: { y: { beginAtZero: true } },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: `Service Status (${selectedMonthName} ${selectedYear})`,
      },
    },
  };

  return (
    <div
      className="p-3 p-md-4 d-flex flex-column"
      style={{ height: "100vh", overflow: "hidden" }}
    >
      <style>
        {`
          html, body, #root {
            height: 100%;
            margin: 0;
            overflow: hidden;
          }
          .dashboard-chart {
            height: 470px;
          }
          .dashboard-chart canvas {
            max-width: 90%;
            height: 90% !important;
          }
          .chart-container {
            height: auto;
          }
        `}
      </style>

      {/* Stats Cards */}
      <div className="row g-3 flex-shrink-0">
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card card-body border-0 shadow-sm p-3 h-100">
            <div className="d-flex gap-3 align-items-center">
              <FaTools className="text-primary fs-4" />
              <h6 className="mb-0">Total Services</h6>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-3 bg-light rounded-3 p-3 shadow-sm">
              <div className="text-center flex-fill border-end">
                <h6 className="text-muted mb-1">Today</h6>
                <h4 className="fw-bold text-primary mb-0">{todayCount}</h4>
              </div>
              <div className="text-center flex-fill">
                <h6 className="text-muted mb-1">{currentMonth}</h6>
                <h4 className="fw-bold text-success mb-0">{monthCount}</h4>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card card-body border-0 shadow-sm p-3 h-100">
            <div className="d-flex gap-3 align-items-center">
              <FaStore className="fs-4 text-primary" />
              <h6 className="mb-0">Brand</h6>
            </div>
            <div className="d-flex flex-column justify-content-center align-items-center mt-3 p-3 bg-light rounded-3 shadow-sm">
              <h6 className="text-muted mb-1">Total</h6>
              <h4 className="fw-bold text-primary mb-0">
                {getBrandData.length}
              </h4>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card card-body border-0 shadow-sm p-3 h-100">
            <div className="d-flex gap-3 align-items-center">
              <FaUserTie className="fs-4 text-primary" />
              <h6 className="mb-0">Staffs</h6>
            </div>
            <div className="d-flex flex-column justify-content-center align-items-center mt-3 p-3 bg-light rounded-3 shadow-sm">
              <h6 className="text-muted mb-1">Total</h6>
              <h4 className="fw-bold text-primary mb-0">
                {getStaffData.length}
              </h4>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-lg-3">
          <div className="card card-body border-0 shadow-sm p-3 h-100">
            <div className="d-flex gap-3 align-items-center">
              <FaUserFriends className="fs-4 text-primary" />
              <h6 className="mb-0">Customers</h6>
            </div>
            <div className="d-flex flex-column justify-content-center align-items-center mt-3 p-3 bg-light rounded-3 shadow-sm">
              <h6 className="text-muted mb-1">Total</h6>
              <h4 className="fw-bold text-primary mb-0">
                {getCustomerDta.length}
              </h4>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="row mt-4 flex-grow-1" style={{ minHeight: 0 }}>
        <div className="col-lg-8 chart-container">
          <div className="card card-body border-0 shadow-sm p-3 dashboard-chart">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-3">Service Report</h6>
              </div>
              <div>
                <input
                  type="month"
                  className="form-control"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  placeholder="Month and Year"
                />
              </div>
            </div>
            <Bar
              data={barData || { labels: [], datasets: [] }}
              options={barOptions}
            />
          </div>
        </div>

        <div className="col-lg-4 chart-container">
          <div className="card card-body border-0 shadow-sm p-2 dashboard-chart d-flex justify-content-center align-items-center">
            <h6 className="mb-3">Service Status</h6>
            <Pie
              data={pieData || { labels: [], datasets: [] }}
              options={pieOptions}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
